import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Logo from "../images/school_logo.png";
import Loader from "./Loader";
import SuccessModal from "./SuccessModal";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import 'jspdf-autotable';
import { db } from "../config/fbConfig";
import { getDoc, doc } from "firebase/firestore";
import { useUserAuth } from "../context/UserAuthContext";
import {SCHOOL_ACRONYM } from '../Variables.js';

const AttendanceIndividual = () => {
  const { classId, kidId } = useParams();
  const [searchParams] = useSearchParams();
  const { userData } = useUserAuth();

  const [student, setStudent] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState({});
  const [currentWeek, setCurrentWeek] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const totalWeeks = 16;

  const calculateAttendance = () => {
    let totalPresent = 0;
    let totalDays = 0;

    const studentAttendance = attendance || {};

    // Calculate up to currentWeek (set by teacher)
    for (let week = 1; week <= currentWeek; week++) {
      const weekData = studentAttendance[week];
      if (!weekData) continue;

      // Count all days up to current week
      const daysToCount = 5;
      totalDays += daysToCount;

      daysOfWeek.forEach((day) => {
        if (weekData.days && weekData.days[day] === true) totalPresent++;
      });
    }

    const percentage = totalDays === 0 ? 0 : ((totalPresent / totalDays) * 100).toFixed(0);
    return { percentage, present: totalPresent, total: totalDays };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!userData?.status === "Parent" || !userData?.kids?.includes(kidId)) {
        setError("Unauthorized access or invalid kid ID");
        setShowModal(true);
        setLoading(false);
        return;
      }

      try {
        const classDoc = await getDoc(doc(db, "classes", classId));
        if (!classDoc.exists()) throw new Error("Class not found");
        setClassInfo(classDoc.data());

        const studentDoc = await getDoc(doc(db, "students", kidId));
        if (!studentDoc.exists()) throw new Error("Student not found");

        const studentData = studentDoc.data();
        let enrollments = studentData.enrollments || [];
        let enrollment = enrollments.find((e) => e.classId === classId);

        if (!enrollment) {
          throw new Error("Student not enrolled in this class");
        }

        const attendanceData = enrollment.Attendance || [];

        const initialAttendance = {};
        for (let week = 1; week <= totalWeeks; week++) {
          const existingWeek = attendanceData.find((w) => w.week === week);
          const days = daysOfWeek.reduce(
            (acc, day) => ({
              ...acc,
              // Set default to true for present
              [day]: existingWeek && existingWeek.days[day] !== undefined ? existingWeek.days[day] : true,
            }),
            {}
          );
          initialAttendance[week] = {
            week,
            days,
            completed: existingWeek ? existingWeek.completed : false,
          };
        }

        setAttendance(initialAttendance);
        setStudent({
          id: studentDoc.id,
          firstName: studentData.firstName || "",
          lastName: studentData.lastName || "",
          enrollments,
        });

        const firstIncompleteWeek = attendanceData.find((w) => w && !w.completed)?.week || totalWeeks + 1;
        setCurrentWeek(firstIncompleteWeek > totalWeeks ? 1 : firstIncompleteWeek);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Error fetching data: " + error.message);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [classId, kidId, userData]);

  const generateAttendancePDF = () => {
    if (!classInfo || !student) return;

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const headerHeight = 30;

    // === Add Logo ===
    const logoX = 14;
    const logoY = 13;
    const logoWidth = 22;
    const logoHeight = 22;

    doc.addImage(Logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`${classInfo.name} ATTENDANCE RECORD`, margin + 30, margin + 10);

    doc.setFontSize(12);
    doc.text(`Student: ${student.firstName} ${student.lastName}`, margin + 30, margin + 16);
    doc.text(`Session: ${searchParams.get("sessionName")} | Term: ${searchParams.get("termName")}`, margin + 30, margin + 22);
    doc.text(`Up to Week ${currentWeek}`, pageWidth / 2, margin + 28, { align: 'center' });

    const headers = [
      { content: 'Week', styles: { halign: 'center', fillColor: [220, 220, 220], lineWidth: 0.1, lineColor: [0, 0, 0] } },
      ...daysOfWeek.map(day => ({ content: day, styles: { halign: 'center', fillColor: [220, 220, 220], lineWidth: 0.1, lineColor: [0, 0, 0] } })),
      { content: 'Status', styles: { halign: 'center', fillColor: [220, 220, 220], lineWidth: 0.1, lineColor: [0, 0, 0] } }
    ];

    const body = [];
    for (let week = 1; week <= currentWeek; week++) {
      const weekData = attendance[week] || {};
      const row = [
        { content: `Week ${week}`, styles: { halign: 'center', textColor: [0, 0, 0] } },
        ...daysOfWeek.map(day => ({
          content: weekData.days?.[day] ? 'O' : 'X',
          styles: {
            halign: 'center',
            fontStyle: 'bold',
            textColor: weekData.days?.[day] ? [0, 128, 0] : [255, 0, 0]
          }
        })),
        { content: weekData.completed ? 'Completed' : 'Pending', styles: { halign: 'center', textColor: [0, 0, 0] } }
      ];
      body.push(row);
    }

    autoTable(doc, {
      startY: margin + headerHeight,
      head: [headers],
      body: body,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        font: 'helvetica'
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        font: 'helvetica'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
        font: 'helvetica'
      },
      margin: { left: margin }
    });

    const { percentage, present, total } = calculateAttendance();
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Summary', 'Value']],
      body: [
        ['Total Present Days', present],
        ['Total Possible Days', total],
        ['Attendance Percentage', `${percentage}%`]
      ],
      styles: {
        fontSize: 10,
        cellPadding: 4,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        halign: 'center',
        font: 'helvetica'
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        font: 'helvetica'
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60, font: 'helvetica' },
        1: { halign: 'center', cellWidth: 30, font: 'helvetica' }
      },
      margin: { left: margin },
      tableWidth: 90
    });

    const now = new Date();
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    const formattedDate = now.toLocaleDateString('en-US', options);

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text(`Printed on ${formattedDate}`, pageWidth/2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    doc.text(`Generated by ${SCHOOL_ACRONYM} App`, margin, doc.internal.pageSize.getHeight() - 10);

    doc.save(`${student.firstName}_${student.lastName}_Attendance_${searchParams.get("sessionName")}_${searchParams.get("termName")}.pdf`);
  };

  if (loading) {
    return <Loader text="LOADING" />;
  }

  if (!student) {
    return (
      <div className="scroll-fixed" style={{ minHeight: "100vh", padding: "20px" }}>
        <p className="male-female-stat">No student data available</p>
      </div>
    );
  }

  return (
    <>
      <div className="scroll-fixed" style={{ minHeight: "100vh" }}>
        <div className="top-elements row align-center space-between">
          <Link to="/home" className="back-link">
            <i className="fa-solid fa-arrow-left size3"></i>
          </Link>
          <div className="column align-center justify-center">
            <img src={Logo} className="middle-logo" alt="logo" />
          </div>
          <Link to="#" className="home-link" onClick={() => window.location.reload()}>
            <i className="fa-solid fa-rotate-right size4"></i>
          </Link>
        </div>
        <div style={{ maxWidth: "100vw", padding: "20px", marginTop: "80px" }}>
          <p className="male-female-stat">
            ATTENDANCE - {student.firstName} {student.lastName} ({classInfo?.name}, {searchParams.get("sessionName")} - {searchParams.get("termName")} Term)
          </p>
          <div className="row align-center gap-1" style={{ marginBottom: "10px" }}>
            <select
              value={currentWeek}
              onChange={(e) => setCurrentWeek(Number(e.target.value))}
              className='male-female-stat'
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((week) => (
                <option className='male-female-stat' key={week} value={week}>
                  Week {week}{attendance[week]?.completed && " ✓"}
                </option>
              ))}
            </select>
            <button
              onClick={generateAttendancePDF}
              className='male-female-stat'
              style={{
                padding: "8px 16px",
                backgroundColor: "rgb(224, 193, 107)",
                color: "#111d2b",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "10px",
              }}
            >
              Print
            </button>
          </div>
          <div className="scroll-fixed scroll-fixed1" style={{ width: "100%", marginTop: "10px" }}>
            <table
              className='male-female-stat'
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.8rem",
                minWidth: "300px",
              }}
            >
              <thead>
                <tr className="fees-table" style={{ backgroundColor: "#f2f2f2" }}>
                  <th style={{ padding: "4px", border: "1px solid #ddd", textAlign: "center", width: "30px" }}>Week</th>
                  {daysOfWeek.map((day) => (
                    <th
                      key={day}
                      style={{ padding: "4px", border: "1px solid #ddd", textAlign: "center", width: "30px" }}
                    >
                      {day}
                    </th>
                  ))}
                  <th style={{ padding: "4px", border: "1px solid #ddd", textAlign: "center", width: "60px" }}>%</th>
                  <th style={{ padding: "4px", border: "1px solid #ddd", textAlign: "center", width: "60px" }}>Days</th>
                </tr>
              </thead>
              <tbody>
                <tr className='male-female-stat' style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "4px", textAlign: "center" }}>Week {currentWeek}</td>
                  {daysOfWeek.map((day) => (
                    <td key={day} style={{ padding: "4px", textAlign: "center" }}>
                      {attendance[currentWeek]?.days[day] ? '✓' : '✗'}
                    </td>
                  ))}
                  <td style={{ padding: "4px", textAlign: "center" }}>
                    {calculateAttendance().percentage}%
                  </td>
                  <td style={{ padding: "4px", textAlign: "center" }}>
                    {calculateAttendance().present}/{calculateAttendance().total}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error}
          onClose={() => {
            setError("");
            setShowModal(false);
          }}
        />
      )}
    </>
  );
};

export default AttendanceIndividual;